import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { IconPhoto, IconUpload } from "@tabler/icons-react";
import { Button } from "@/components/lilia-ui/button";
import { Progress } from "@/components/lilia-ui/progress";
import { useApiContext, API_BASE } from "@/contexts/ApiContext";
import { useEditorStore } from "@/store/editorStore";
import { BlockEditModal } from "../BlockEditModal";
import { FullWidthSwitch } from "../FullWidthSwitch";
import { resolveImageSrc } from "@/lib/placeholder-image";
import styles from "../blocks/blocks.module.scss";
import { testIds } from "@/testIds";
import { LabelChip } from "../LabelChip";
import { deriveLabel } from "@/lib/labels/labelKeys";
import { documentLabels } from "@/lib/labels/documentLabels";
import { useThemeStore } from "@/store/themeStore";
import { MODE_DARK } from "@/constants/theme.constant";
import {
  isTikz,
  mayBeTransparent,
  takeTikzSplitRequest,
  TIKZ_TOGGLE_SOURCE_EVENT,
  type TikzToggleDetail,
} from "@/lib/tikzFigure";
import {
  TIKZ_FIGURE_ACTION_EVENT,
  clearFigureScope,
  figureName as nameOfFigure,
  outlineOf,
  publishFigure,
  setFigureScope,
  suggestionFor,
  unpublishFigure,
  useTikzAsk,
  type FigureActionDetail,
} from "@/lib/tikzAsk";
import { prefillAsk } from "@/lib/askPrefill";
import { PaperPanel, TikzFigure } from "./tikz/TikzFigure";
import { useTikzDrawing } from "./tikz/useTikzDrawing";
import { TikzSplitView } from "./tikz/TikzSplitView";
import { useDocumentPreamble } from "./tikz/useDocumentPreamble";
import { useLinkedPlot, writeSourceOutsideHistory } from "./tikz/useLinkedPlot";
import { docTables, readPlotSpec, tableData, problemText, type PlotSpec } from "@/lib/tikzPlot";
import type { SplitPlot } from "./tikz/TikzSplitView";

export function FigureNodeView({ node, updateAttributes, selected, deleteNode, editor, getPos }: NodeViewProps) {
  const { fetchWithAuth } = useApiContext();
  const documentId = useEditorStore((s) => s.documentId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draftCaption, setDraftCaption] = useState("");
  const [draftLabel, setDraftLabel] = useState("");
  const [draftAlt, setDraftAlt] = useState("");
  const [draftPlacement, setDraftPlacement] = useState("htbp");
  const [draftFullWidth, setDraftFullWidth] = useState(false);
  const [draftSubfigures, setDraftSubfigures] = useState<Array<{ src: string; caption: string }>>([]);
  const subfigureInputRef = useRef<HTMLInputElement>(null);

  const src = (node.attrs.src as string) || "";
  const caption = (node.attrs.caption as string) || "";
  const label = (node.attrs.label as string) || "";
  const alt = (node.attrs.alt as string) || "";
  const placement = (node.attrs.placement as string) || "htbp";
  const subfigures = (node.attrs.subfigures as Array<{ src: string; caption: string }>) || [];
  const blockId = (node.attrs.blockId as string) || "";
  const dark = useThemeStore((s) => s.mode) === MODE_DARK;

  // TikZ figures (step 1): the source is drawn by the server. A figure with no
  // caption is a bare picture — inline, no caption, no number — until the
  // author makes it a numbered figure. Once a caption shows it keeps showing,
  // so erasing it while typing doesn't make the field vanish under the caret.
  const tikz = isTikz(node.attrs);
  const source = (node.attrs.source as string) || "";
  // A plot linked to a table (step 4a) is written again from its table. Kept
  // out of the undo history: Undo undoes the table edit, and the plot follows.
  const writeLinkedSource = useCallback(
    (next: string, plot?: PlotSpec) => {
      const pos = typeof getPos === "function" ? getPos() : undefined;
      if (!editor) return;
      const before = typeof pos === "number" ? ((editor.state.doc.nodeAt(pos)?.attrs.source as string) ?? "") : "";
      writeSourceOutsideHistory(editor, pos, next, plot);
      // "Updated just now", for 4 s, when the table changed a plot that was already drawn.
      if (before.trim() && before !== next) setPlotUpdatedAt(Date.now());
    },
    [editor, getPos],
  );
  const [plotUpdatedAt, setPlotUpdatedAt] = useState(0);
  useEffect(() => {
    if (!plotUpdatedAt) return;
    const t = setTimeout(() => setPlotUpdatedAt(0), 4000);
    return () => clearTimeout(t);
  }, [plotUpdatedAt]);
  const plotSpec = tikz ? readPlotSpec(node.attrs.plot) : null;
  const linkedPlot = useLinkedPlot(tikz ? editor : null, node.attrs.plot, source, writeLinkedSource);
  // A series whose column left the table: said in the Plot tab until the split view closes.
  const [droppedSeries, setDroppedSeries] = useState<string[]>([]);
  useEffect(() => {
    if (linkedPlot?.state === "linked" && linkedPlot.dropped.length) setDroppedSeries((d) => [...new Set([...d, ...linkedPlot.dropped])]);
  }, [linkedPlot]);
  const [splitView, setSplitView] = useState<"plot" | "source">("source");
  const [showCaption, setShowCaption] = useState(!tikz || !!caption.trim());
  useEffect(() => {
    if (caption.trim()) setShowCaption(true);
  }, [caption]);
  const bare = tikz && !showCaption;
  const [view, setView] = useState<"drawing" | "source">("drawing");
  const [highlightLine, setHighlightLine] = useState<number | null>(null);
  // The split view (step 3): opened by Source → Edit, ⋯ → Edit source, ⌥⏎
  // twice, or straight away for a TikZ figure just inserted (its gallery).
  const [splitOpen, setSplitOpen] = useState(false);
  // Taken in an effect, and only ever to open: a double-run (StrictMode) can't close it.
  useEffect(() => {
    if (tikz && takeTikzSplitRequest(source)) setSplitOpen(true);
    // On mount only: a figure emptied later doesn't open on its own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The split view's newest good drawing: the canvas shows it rather than drawing again.
  const [known, setKnown] = useState<{ hash: string; svg: string } | null>(null);
  const drawing = useTikzDrawing(fetchWithAuth, documentId, tikz ? blockId : null, tikz ? source : "", {
    paused: splitOpen,
    known,
  });
  const preamble = useDocumentPreamble(documentId);

  // Ask Lilia on this figure (1c) -------------------------------------------------------
  //
  // The figure tells the dock what it is and how it stands ("Figure 3",
  // drawn, an error on line 5); while its split view is open, the split view
  // says it instead. Selecting it scopes the dock to it.
  const outline = tikz && editor?.state ? outlineOf(editor.state.doc) : [];
  const figureName = tikz ? nameOfFigure(outline, blockId) : "Figure";
  const canvasState = !source.trim() ? "empty" : drawing.error && !drawing.loading ? "error" : "drawn";
  const canvasError = drawing.error && !drawing.loading ? drawing.error : null;
  useEffect(() => {
    if (!tikz || !blockId || !documentId || splitOpen) return;
    publishFigure({
      documentId,
      blockId,
      name: figureName,
      source,
      caption,
      label,
      state: canvasState,
      error: canvasError ? { line: canvasError.line, message: canvasError.message } : null,
      splitOpen: false,
    });
  }, [tikz, blockId, documentId, splitOpen, figureName, source, caption, label, canvasState, canvasError]);
  useEffect(() => {
    if (!tikz || !blockId) return;
    return () => unpublishFigure(blockId);
  }, [tikz, blockId]);
  useEffect(() => {
    if (!tikz || !blockId || !documentId) return;
    // The split view scopes it while open; back on the canvas, still selected, the selection does.
    if (selected && !splitOpen) setFigureScope({ documentId, blockId, afterBlockId: null, section: null, owner: "selection" });
    else if (!selected) clearFigureScope("selection", blockId);
  }, [tikz, blockId, documentId, selected, splitOpen]);
  const askLilia = (intent: "change" | "explain") => {
    if (!documentId) return;
    setFigureScope({ documentId, blockId, afterBlockId: null, section: null, owner: "menu" });
    prefillAsk(suggestionFor(intent).prefill);
  };
  // Explain's line chips, on the canvas's read-only source.
  const aiHighlight = useTikzAsk((s) => (s.highlight?.blockId === blockId ? s.highlight : null));
  // What the dock asks of this figure: open the split view (a change to
  // review), take a caption or a drawing, bring lines into view.
  const actionRef = useRef<(d: FigureActionDetail) => void>(() => {});
  actionRef.current = (d) => {
    const a = d.action;
    if (a.type === "open-split") {
      setView("drawing");
      setSplitOpen(true);
    } else if (a.type === "set-caption") {
      setShowCaption(true);
      updateAttributes({ caption: a.caption });
    } else if (a.type === "set-source") {
      updateAttributes({ source: a.source });
    } else if (a.type === "scroll-to-line" && !splitOpen) {
      setHighlightLine(a.line);
      setView("source");
    }
  };
  useEffect(() => {
    if (!tikz || !blockId) return;
    const onAction = (e: Event) => {
      const d = (e as CustomEvent<FigureActionDetail>).detail;
      if (d?.blockId === blockId) actionRef.current(d);
    };
    window.addEventListener(TIKZ_FIGURE_ACTION_EVENT, onAction);
    return () => window.removeEventListener(TIKZ_FIGURE_ACTION_EVENT, onAction);
  }, [tikz, blockId]);

  const toggleSource = () => {
    setHighlightLine(null);
    setView((v) => (v === "source" ? "drawing" : "source"));
  };
  const openSplit = (initial: "plot" | "source" = "source") => {
    setView("drawing");
    setSplitView(initial);
    setSplitOpen(true);
  };
  const selectTable = () => {
    if (!plotSpec || !editor) return;
    editor.state.doc.forEach((n, offset) => {
      if (n.attrs.blockId === plotSpec.table) editor.chain().setNodeSelection(offset).focus(undefined, { scrollIntoView: true }).run();
    });
  };
  const detachPlot = () => updateAttributes({ plot: null });
  // Before the first look at the document (null), it is linked: never a flash of "Table removed".
  const plotState = !plotSpec ? null : linkedPlot?.state === "missing" ? "removed" : linkedPlot?.state === "problem" ? "problem" : "linked";
  const plotTableName = linkedPlot && linkedPlot.state !== "missing" ? linkedPlot.table : "a table";
  const splitPlot = (): SplitPlot | undefined => {
    if (!plotSpec) return undefined;
    const t = editor?.state ? docTables(editor.state.doc).find((x) => x.blockId === plotSpec.table) : undefined;
    return {
      table: t ? tableData(t) : null,
      spec: plotSpec,
      tableName: plotTableName,
      problem: linkedPlot?.state === "problem" ? problemText(linkedPlot.problem, plotTableName === "a table" ? "This table" : plotTableName) : null,
      dropped: droppedSeries,
      initialView: splitView,
      onSpecChange: (spec) => updateAttributes({ plot: spec }),
      onDetach: detachPlot,
    };
  };
  // Done or Esc: back to the figure on the canvas, selected.
  const closeSplit = useCallback(() => {
    setSplitOpen(false);
    setDroppedSeries([]);
    const pos = typeof getPos === "function" ? getPos() : undefined;
    if (typeof pos === "number") editor?.chain?.().setNodeSelection(pos).focus(undefined, { scrollIntoView: true }).run();
  }, [editor, getPos]);
  // ⌥⏎ with the figure selected (the editor's shortcut names this figure's
  // position): the source; again, the split view.
  const altEnter = () => {
    if (view === "source") openSplit();
    else toggleSource();
  };
  const toggleRef = useRef(altEnter);
  toggleRef.current = altEnter;
  useEffect(() => {
    if (!tikz) return;
    const dom = editor?.view?.dom;
    if (!dom) return;
    const onToggle = (e: Event) => {
      const pos = typeof getPos === "function" ? getPos() : undefined;
      if ((e as CustomEvent<TikzToggleDetail>).detail?.pos === pos) toggleRef.current();
    };
    dom.addEventListener(TIKZ_TOGGLE_SOURCE_EVENT, onToggle);
    return () => dom.removeEventListener(TIKZ_TOGGLE_SOURCE_EVENT, onToggle);
  }, [tikz, editor, getPos]);

  const PLACEMENT_OPTIONS = [
    { value: "htbp", label: "Auto (htbp)" },
    { value: "H", label: "Here (H)" },
    { value: "t", label: "Top (t)" },
    { value: "b", label: "Bottom (b)" },
    { value: "p", label: "Page (p)" },
  ];

  const handleCaptionChange = (e: React.FormEvent<HTMLElement>) => {
    updateAttributes({ caption: e.currentTarget.textContent || "" });
  };

  // The inline caption is the author's typing surface, so React does not
  // render its text: every keystroke updates the node, and re-rendering the
  // text under the caret put the caret back at the start — a caption typed
  // on the canvas came out backwards. It is filled from the node instead,
  // whenever the caption changed elsewhere (the dialog, undo) and the author
  // is not typing in it.
  const captionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = captionRef.current;
    if (el && document.activeElement !== el && el.textContent !== caption) el.textContent = caption;
  }, [caption, showCaption]);

  // "Make a numbered figure": the caption field appears, ready to type in.
  const [focusCaption, setFocusCaption] = useState(false);
  useEffect(() => {
    if (!focusCaption) return;
    setFocusCaption(false);
    captionRef.current?.focus();
  }, [focusCaption]);
  const makeNumbered = () => {
    setShowCaption(true);
    setFocusCaption(true);
  };

  // Every other label in the document, live — this figure's own excluded.
  const otherLabels = () =>
    documentLabels(editor.state.doc, typeof getPos === "function" ? getPos() : undefined);

  /**
   * The inline caption commits on every keystroke (onInput), so a label is
   * derived here, on blur — when the caption is finished. Deriving on input
   * would fix the key at "fig:m" after the first letter, and derivation is
   * one-way, so it would never be corrected.
   */
  const handleCaptionCommit = () => {
    if (label) return;
    const derived = deriveLabel(caption, "figure", new Set(otherLabels().map((o) => o.key)));
    if (derived) updateAttributes({ label: derived });
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !documentId) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be smaller than 10MB");
      return;
    }

    setIsUploading(true);
    setUploadProgress(20);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const uploadResponse = await fetchWithAuth(`${API_BASE}/documents/${documentId}/assets/upload`, {
        method: "POST",
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorBody = await uploadResponse.json().catch(() => ({ message: "Upload failed" }));
        throw new Error(errorBody.message || `Upload failed (${uploadResponse.status})`);
      }

      setUploadProgress(80);

      const asset = await uploadResponse.json();

      updateAttributes({
        src: asset.url,
        alt: file.name,
        assetId: asset.id,
      });

      setUploadProgress(100);
    } catch (err) {
      console.error("[FigureNodeView] Upload error:", err);
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = () => {
    updateAttributes({ src: "", alt: "", assetId: null });
  };

  const handleOpenModal = () => {
    setDraftCaption(caption);
    setDraftLabel(label);
    setDraftAlt(alt);
    setDraftPlacement(placement);
    setDraftFullWidth(node.attrs.span === "page");
    setDraftSubfigures([...subfigures]);
    setIsModalOpen(true);
  };

  const modalOthers = isModalOpen ? otherLabels() : [];
  // Offered only while the figure has no label.
  const derivedLabel = draftLabel
    ? null
    : deriveLabel(draftCaption, "figure", new Set(modalOthers.map((o) => o.key)));

  const handleSaveModal = () => {
    if (draftCaption.trim()) setShowCaption(true);
    updateAttributes({
      caption: draftCaption,
      alt: draftAlt,
      placement: draftPlacement,
      span: draftFullWidth ? "page" : null,
      // A TikZ figure has no sub-figures; its source is edited in the split view.
      ...(tikz ? {} : { subfigures: draftSubfigures }),
      // Derived only when the figure has none; once set, a caption rename
      // leaves it alone, because something may already point at it.
      label: draftLabel || derivedLabel || null,
    });
    setIsModalOpen(false);
  };

  const handleAddSubfigure = () => {
    subfigureInputRef.current?.click();
  };

  const handleSubfigureFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/") || !documentId) return;
    if (file.size > 10 * 1024 * 1024) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/assets/upload`, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) return;
      const asset = await response.json();
      setDraftSubfigures([...draftSubfigures, { src: asset.url, caption: "" }]);
    } catch (err) {
      console.error("[FigureNodeView] Subfigure upload error:", err);
    }
    if (subfigureInputRef.current) subfigureInputRef.current.value = "";
  };

  /**
   * The split view goes over the workspace the canvas sits in (the host the
   * page marks), so the top bar and the Ask Lilia dock stay; over the window
   * when there is none.
   */
  const splitSheet = () => {
    const host = (editor?.view?.dom as HTMLElement | undefined)?.closest?.<HTMLElement>("[data-tikz-sheet-host]") ?? null;
    return createPortal(
      <TikzSplitView
        fetchWithAuth={fetchWithAuth}
        documentId={documentId!}
        blockId={blockId}
        source={source}
        caption={caption}
        label={label}
        initialSvg={drawing.svg}
        figureName={figureName}
        preamble={preamble}
        fixed={!host}
        onSourceChange={(next) => updateAttributes({ source: next })}
        onCaptionChange={(next) => {
          if (next.trim()) setShowCaption(true);
          updateAttributes({ caption: next });
        }}
        onCaptionCommit={handleCaptionCommit}
        onDrawn={setKnown}
        onDone={closeSplit}
        plot={splitPlot()}
      />,
      host ?? document.body,
    );
  };

  return (
    <NodeViewWrapper
      data-type="figure-block"
      data-testid={testIds.figure.block}
      data-block-id={node.attrs.blockId}
      data-figure-kind={tikz ? "tikz" : "image"}
      className={selected ? "ProseMirror-selectednode" : ""}
    >
      <figure className={styles.figure} data-bare={bare ? "true" : undefined}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        {tikz ? (
          <TikzFigure
            blockId={blockId}
            source={source}
            drawing={drawing}
            bare={bare}
            selected={selected}
            dark={dark}
            alt={alt || caption}
            view={view}
            highlightLine={aiHighlight && view === "source" ? aiHighlight.from : highlightLine}
            highlightTo={aiHighlight && view === "source" ? aiHighlight.to : null}
            onToggleSource={toggleSource}
            onShowSource={(line) => {
              setHighlightLine(line);
              setView("source");
            }}
            onEditSource={() => openSplit("source")}
            onDetails={handleOpenModal}
            onMakeNumbered={makeNumbered}
            onAsk={documentId ? askLilia : undefined}
            plot={
              plotState
                ? {
                    table: plotTableName,
                    state: plotState,
                    updated: !!plotUpdatedAt,
                    onSettings: () => openSplit("plot"),
                    onSelectTable: selectTable,
                    onDetach: detachPlot,
                  }
                : undefined
            }
          />
        ) : src ? (
          <div className={styles.figureImageContainer}>
            {dark && mayBeTransparent(src) ? (
              <PaperPanel dark testId={testIds.figure.paperPanel}>
                <img src={resolveImageSrc(src)} alt={alt} className={styles.figureImage} />
              </PaperPanel>
            ) : (
              <img src={resolveImageSrc(src)} alt={alt} className={styles.figureImage} />
            )}
            <div className={styles.figureOverlay}>
              <Button variant="subtle" onClick={handleUploadClick} title="Replace image">
                <IconUpload size={16} /> Replace
              </Button>
              <Button variant="subtle" onClick={handleOpenModal} title="Edit figure details" data-testid={testIds.figure.edit}>
                Edit
              </Button>
              <Button variant="subtle" onClick={handleRemoveImage} title="Remove image">
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.figurePlaceholder} onClick={handleUploadClick}>
            {isUploading ? (
              <div className={styles.uploadProgress}>
                <Progress value={uploadProgress} />
                <span>Uploading...</span>
              </div>
            ) : (
              <>
                <Button variant="subtle">
                  <IconPhoto size={16} /> Upload Image
                </Button>
                <span className={styles.uploadHint}>Click or drag and drop an image</span>
              </>
            )}
          </div>
        )}

        {error && <div className={styles.figureError}>{error}</div>}

        {!bare && (
          <figcaption
            ref={captionRef}
            data-testid={testIds.figure.caption}
            className={styles.figureCaption}
            contentEditable
            suppressContentEditableWarning
            onInput={handleCaptionChange}
            onBlur={handleCaptionCommit}
            data-placeholder="Add caption..."
          />
        )}

        <BlockEditModal
          sourceNode={node}
          title={tikz ? "Edit TikZ figure" : "Edit Figure"}
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveModal}
          width={640}
        >
          {!tikz && src && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <img src={resolveImageSrc(src)} alt={draftAlt} style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 4 }} />
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Label</label>
            <LabelChip
              value={draftLabel}
              derived={derivedLabel}
              others={modalOthers}
              onChange={setDraftLabel}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Caption</label>
            <textarea
              data-testid={testIds.figure.editCaption}
              value={draftCaption}
              onChange={(e) => setDraftCaption(e.target.value)}
              placeholder="Enter caption..."
              rows={3}
              style={{
                width: "100%", padding: "8px 12px", fontSize: 13,
                border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4, resize: "vertical",
              }}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Alt Text</label>
            <input
              type="text"
              value={draftAlt}
              onChange={(e) => setDraftAlt(e.target.value)}
              placeholder="Describe the image for accessibility..."
              style={{
                width: "100%", padding: "8px 12px", fontSize: 13,
                border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
              }}
            />
          </div>
          {/* Placement dropdown */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Placement</label>
            <select
              value={draftPlacement}
              onChange={(e) => setDraftPlacement(e.target.value)}
              style={{
                padding: "6px 10px", fontSize: 13,
                border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
                background: "var(--lilia-bg, #fff)", color: "var(--lilia-text, #334155)",
              }}
            >
              {PLACEMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <FullWidthSwitch value={draftFullWidth} onChange={setDraftFullWidth} />
          {/* Subfigures (an image figure's) */}
          <div hidden={tikz}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Subfigures</label>
            <input
              ref={subfigureInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleSubfigureFileSelect}
            />
            {draftSubfigures.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {draftSubfigures.map((sf, i) => (
                  <div key={i} style={{ width: 140, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4, padding: 6, position: "relative" }}>
                    <img src={resolveImageSrc(sf.src)} alt="" style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 2 }} />
                    <input
                      type="text"
                      value={sf.caption}
                      onChange={(e) => {
                        const updated = [...draftSubfigures];
                        updated[i] = { ...updated[i], caption: e.target.value };
                        setDraftSubfigures(updated);
                      }}
                      placeholder="Caption..."
                      style={{
                        width: "100%", marginTop: 4, padding: "4px 6px", fontSize: 11,
                        border: "1px solid var(--lilia-border, #ddd)", borderRadius: 3,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setDraftSubfigures(draftSubfigures.filter((_, idx) => idx !== i))}
                      style={{
                        position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,0.5)",
                        color: "#fff", border: "none", borderRadius: "50%", width: 18, height: 18,
                        fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                      title="Remove subfigure"
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Button variant="subtle" onClick={handleAddSubfigure}>
              + Add subfigure
            </Button>
          </div>
        </BlockEditModal>
      </figure>
      {tikz && splitOpen && documentId && splitSheet()}
    </NodeViewWrapper>
  );
}
